<template>
  <spinner v-if="loading"></spinner>
  <transition name="fade" v-else>
    <div v-if="!error" class="container">
      <force-league-update :summoner="summoner"></force-league-update>
      <summoner-banner :summoner="summoner"></summoner-banner>
      <div class="columns is-multiline">
        <div class="column is-12-tablet is-5-desktop">
          <gain-loss-graph v-if="dailyDifferences.length" :differences="dailyDifferences"></gain-loss-graph>
        </div>
        <div class="column is-12-tablet is-7-desktop">
          <div class="box box--highlight" v-if="!hasLeaguePositions" style="padding-top: 2rem;padding-bottom: 2rem;">
            <h2 class="title is-4 is-primary" style="text-transform: uppercase;">No tracked {{ queueName }} queue games</h2>
            <div class="content">
              <p>
                The summoner just got added to the system or hasn't played any ranked games yet.<br>
              </p>
              <p>
                <strong>Come back later!</strong>
              </p>
            </div>
          </div>
          <div v-if="hasLeaguePositions" class="league-positions">
            <league-position
              v-for="leaguePosition in leaguePositions"
              :key="leaguePosition.id"
              :league-position="leaguePosition"
            ></league-position>
            <paginator
              :paginator="leaguePositionsPaginator"
              @loadMoreItems="loadMoreItems"
              :loading="paginatorLoading"
            ></paginator>
          </div>
        </div>
        <div class="column is-12">
          <league-points-history-graph v-if="leaguePointsHistory.length > 1" :history="leaguePointsHistory"></league-points-history-graph>
        </div>
      </div>
    </div>
    <div v-if="error" class="container">
      <div class="box box--error" v-if="errorDetails.status === 404">
        <div class="columns is-vcentered">
          <div class="column is-narrow">
            <img src="../assets/tier-icons/challenger.png" alt="error image">
          </div>
          <div class="column">
            <h2 class="title is-5 is-danger">Could not find the summoner!</h2>
            <p>Didn't find the summoner <strong>{{ errorDetails.response.name }} @ {{ errorDetails.response.region.name }}</strong>.</p>
            <p>Please check the spelling and the region.</p>
            <summoner-search style="margin-top: 1.5rem;"></summoner-search>
            <div class="content is-small" style="margin-top: 0.5rem;">
              <p>The search is <strong>not</strong> case sensitive.</p>
            </div>
          </div>
        </div>
      </div>
      <div class="box box--error" v-else>
        <div class="columns is-vcentered">
          <div class="column is-narrow">
            <img src="../assets/tier-icons/bronze.png" alt="error image">
          </div>
          <div class="column">
            <h2 class="title is-5 is-danger">The server responded with an error!</h2>
            <p>Please try it again.</p>
            <summoner-search style="margin-top: 1.5rem;"></summoner-search>
            <div class="content is-small" style="margin-top: 0.5rem;">
              <p>The search is <strong>not</strong> case sensitive.</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  </transition>
</template>

<script>
import moment from 'moment'
import io from 'socket.io-client'
import Echo from 'laravel-echo'

import * as api from '@/api'
import SummonerSearch from '@/components/SummonerSearch'
import ForceLeagueUpdate from '@/components/ForceLeagueUpdate'
import SummonerBanner from '@/components/SummonerBanner'
import LeaguePosition from '@/components/LeaguePosition'
import GainLossGraph from '@/components/GainLossGraph'
import Paginator from '@/components/Paginator'
import LeaguePointsHistoryGraph from '@/components/LeaguePointsHistoryGraph'
import Spinner from '@/components/Spinner'

window.io = io

export default {
  data: () => ({
    loading: true,
    paginatorLoading: false,
    summoner: {},
    leaguePointsHistory: [],
    error: false,
    errorDetails: {},
  }),

  computed: {
    leaguePositions () {
      return this.summoner.league_positions.data
    },
    leaguePositionsPaginator () {
      return this.summoner.league_positions.meta.pagination
    },
    dailyDifferences () {
      return this.summoner.daily_differences.data
    },
    hasLeaguePositions () {
      return this.leaguePositions.length >= 1
    },
    region () {
      return this.$route.params.region
    },
    summonerName () {
      return this.$route.params.name
    },
    queue () {
      if (this.$route.meta.queue === 'flex') {
        return 'RANKED_FLEX_SR'
      }

      return 'RANKED_SOLO_5x5'
    },
    queueName () {
      if (this.$route.meta.queue === 'flex') {
        return 'flex'
      }

      return 'solo'
    },
  },

  components: {
    SummonerSearch,
    ForceLeagueUpdate,
    SummonerBanner,
    LeaguePosition,
    GainLossGraph,
    Paginator,
    LeaguePointsHistoryGraph,
    Spinner,
  },

  created () {
    window.Echo = new Echo({
      broadcaster: 'socket.io',
      host: process.env.WS_URL,
    })
  },

  mounted () {
    this.loadSummoner()
  },

  watch: {
    '$route' () {
      window.Echo.leave(`leaguepoints_database_summoner:${this.summoner.summoner_id}`)

      this.loadSummoner()
    },
  },

  methods: {
    loadSummoner () {
      this.loading = true
      this.leaguePointsHistory = []
      this.error = false
      this.errorDetails = {}

      api.summonerByName(this.summonerName, this.region, ['league_positions', 'daily_differences'], this.queue).then(({ data: { data: summoner } }) => {
        this.summoner = summoner
        this.loading = false
        this.loadLeaguePointsHistory()

        this.listenForUpdates(summoner.summoner_id)
      }).catch(({ response }) => {
        this.error = true
        this.errorDetails.response = response.data.data
        this.errorDetails.status = response.status
        this.loading = false
      })
    },

    listenForUpdates (summonerId) {
      console.log('listening for summoner ' + summonerId)
      window.Echo.channel(`leaguepoints_database_summoner:${summonerId}`)
        .listen('LeaguePositionUpdated', this.updateSummoner)
        .listen('LeaguePositionUpdateAttempted', (b, e) => {
          this.summoner.league_update_attempted_at = moment().subtract(1, 'seconds').toISOString()
          this.summoner.in_update_queue = false
        })
        .listen('QueuedForLeaguePositionUpdate', (b, e) => {
          this.summoner.in_update_queue = true
        })
    },

    updateSummoner () {
      console.log('Starting summoner update...')
      api.summonerByName(this.summonerName, this.region, ['league_positions', 'daily_differences'], this.queue).then(({ data: { data: summoner } }) => {
        this.summoner = summoner
        this.loadLeaguePointsHistory()
      })
    },

    loadMoreItems (nextPage) {
      this.paginatorLoading = true

      api.summonerByName(this.summonerName, this.region, [`league_positions`], this.queue, nextPage).then(({ data: { data: summoner } }) => {
        this.summoner.league_positions.data = this.summoner.league_positions.data.concat(summoner.league_positions.data)
        this.summoner.league_positions.meta = summoner.league_positions.meta
        this.paginatorLoading = false
      })
    },

    loadLeaguePointsHistory () {
      api.leaguePointsHistory(this.summonerName, this.region, this.queue).then(({ data: { data: leaguePositions } }) => {
        this.leaguePointsHistory = leaguePositions
      })
    },
  },
}
</script>



// WEBPACK FOOTER //
// Summoner.vue?0d77a231